import { useEffect, useState, type FormEvent } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'

import {
    useGetCollectionDomainQuery,
    useRefreshCollectionDomainMutation,
    useRemoveCollectionDomainMutation,
    useSetCollectionDomainMutation,
    type CollectionDomainState,
} from '@/apis/collectionDomainApi'
import { useListProjectsQuery } from '@/apis/projectsApi'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { CopyButton } from '@/components/shared/CopyButton'
import { useProjectId } from '@/hooks/useReportArgs'
import { ReportStatus } from '@/modules/reports/common'

/** Status polling cadence while a hostname is still pending. */
const POLL_MS = 10_000
const DOCS_URL =
    'https://github.com/aapastech/yourownmetrics/blob/develop/docs/first-party.md#b-reverse-proxy-on-your-own-origin'

function Snippet({ code }: { code: string }) {
    return (
        <Box
            sx={{
                position: 'relative',
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1.5,
                pr: 5,
                fontFamily: 'monospace',
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
            }}
        >
            {code}
            <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                <CopyButton text={code} />
            </Box>
        </Box>
    )
}

function scriptTag(src: string, endpoint: string) {
    return `<script defer src="${src}" data-key="yom_pk_…" data-endpoint="${endpoint}"></script>`
}

function CustomHostnameCard({
    projectId,
    state,
}: {
    projectId: string
    state: CollectionDomainState
}) {
    const [hostname, setHostname] = useState('')
    const [confirmRemove, setConfirmRemove] = useState(false)
    const [setDomain, setRes] = useSetCollectionDomainMutation()
    const [refresh, refreshRes] = useRefreshCollectionDomainMutation()
    const [remove, removeRes] = useRemoveCollectionDomainMutation()
    const d = state.domain
    // Nothing to poll while the feature is off: the API can't reach Cloudflare then.
    const pending = !!d && !d.live && state.custom_hostname.available

    // Poll Cloudflare (through the API) until DNS and the certificate are active.
    useEffect(() => {
        if (!pending) {
            return
        }
        const t = setInterval(() => {
            refresh(projectId).catch(() => undefined)
        }, POLL_MS)
        return () => clearInterval(t)
    }, [pending, projectId, refresh])

    const submit = async (e: FormEvent) => {
        e.preventDefault()
        await setDomain({ projectId, hostname: hostname.trim() })
            .unwrap()
            .then(() => setHostname(''))
            .catch(() => undefined)
    }
    const error = v2ErrorMessage(setRes.error ?? refreshRes.error ?? removeRes.error)

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Custom hostname
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Collect on a subdomain you own, e.g. <code>t.example.com</code>. YOM provisions
                    the certificate; you add one CNAME.
                </Typography>
                {!state.custom_hostname.available && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {state.custom_hostname.reason ??
                            'Custom hostnames are not available yet. Use the reverse proxy below.'}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {d ? (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography sx={{ fontWeight: 600 }}>{d.hostname}</Typography>
                            <Chip
                                size="small"
                                color={d.live ? 'success' : 'warning'}
                                label={d.live ? 'Live' : `Pending · ${d.ssl_status ?? d.status}`}
                            />
                            <Box sx={{ flex: 1 }} />
                            <Button
                                size="small"
                                onClick={() => {
                                    refresh(projectId).catch(() => undefined)
                                }}
                                disabled={refreshRes.isLoading}
                            >
                                Check now
                            </Button>
                            <Button
                                size="small"
                                color="error"
                                onClick={() => setConfirmRemove(true)}
                            >
                                Remove
                            </Button>
                        </Box>
                        {!d.live && (
                            <>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Create these DNS records.
                                    {pending ? ' This page checks every few seconds.' : ''}
                                </Typography>
                                <Table size="small" sx={{ mb: 2 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {d.records.map(r => (
                                            <TableRow key={`${r.type}:${r.name}`}>
                                                <TableCell>{r.type}</TableCell>
                                                <TableCell sx={{ wordBreak: 'break-all' }}>
                                                    {r.name} <CopyButton text={r.name} />
                                                </TableCell>
                                                <TableCell sx={{ wordBreak: 'break-all' }}>
                                                    {r.value} <CopyButton text={r.value} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {d.errors.length > 0 && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        {d.errors.join(' · ')}
                                    </Alert>
                                )}
                            </>
                        )}
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Script tag (or <code>{`YOM.init({ endpoint: '${d.endpoint}' })`}</code>
                            ):
                        </Typography>
                        <Snippet code={scriptTag(d.script_src, d.endpoint)} />
                    </>
                ) : (
                    state.custom_hostname.available && (
                        <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                size="small"
                                label="Hostname"
                                placeholder="t.example.com"
                                value={hostname}
                                onChange={e => setHostname(e.target.value)}
                                sx={{ flex: 1 }}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!hostname.trim() || setRes.isLoading}
                            >
                                Add
                            </Button>
                        </Box>
                    )
                )}
            </CardContent>
            <ConfirmDialog
                open={confirmRemove}
                title="Remove collection domain?"
                message={`Events sent to ${d?.hostname ?? 'this hostname'} will stop being accepted. Update your SDK endpoint first.`}
                confirmLabel="Remove"
                confirmColor="error"
                onConfirm={() => {
                    setConfirmRemove(false)
                    remove(projectId).catch(() => undefined)
                }}
                onCancel={() => setConfirmRemove(false)}
            />
        </Card>
    )
}

function ReverseProxyCard({ state }: { state: CollectionDomainState }) {
    const rp = state.reverse_proxy
    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Reverse proxy on your own origin
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Proxy <code>{rp.path}/*</code> on your site to YOM ({rp.upstream}). Works on any
                    plan and needs no DNS change. Recipes for Next.js, Vercel, Cloudflare Workers
                    and nginx:{' '}
                    <a href={DOCS_URL} target="_blank" rel="noreferrer">
                        docs/first-party.md
                    </a>
                    .
                </Typography>
                <Snippet code={scriptTag(`${rp.path}/sdk/latest/yom.min.js`, rp.endpoint)} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    The proxy must pass <code>X-Forwarded-Host</code>, <code>X-Forwarded-For</code>,{' '}
                    <code>User-Agent</code>, <code>Sec-GPC</code> and <code>DNT</code> through, so
                    visitors and opt-outs are seen correctly.
                </Typography>
            </CardContent>
        </Card>
    )
}

/** Settings → Collection domain: first-party collection per project. */
export function CollectionDomainPage() {
    const { data: projects = [] } = useListProjectsQuery()
    const selected = useProjectId()
    const [picked, setPicked] = useState('')
    const projectId = picked || selected || projects[0]?.id || ''
    const { data, isLoading, error } = useGetCollectionDomainQuery(projectId, { skip: !projectId })

    return (
        <Box sx={{ p: 3, maxWidth: 900 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    mb: 3,
                }}
            >
                <Typography variant="h5">Collection domain</Typography>
                <TextField
                    size="small"
                    select
                    label="Project"
                    value={projects.some(p => p.id === projectId) ? projectId : ''}
                    onChange={e => setPicked(e.target.value)}
                    sx={{ minWidth: 240 }}
                >
                    {projects.map(p => (
                        <MenuItem key={p.id} value={p.id}>
                            {p.name}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={!data && !isLoading}
                emptyTitle="Pick a project"
            >
                {data && (
                    <>
                        <CustomHostnameCard projectId={projectId} state={data} />
                        <ReverseProxyCard state={data} />
                    </>
                )}
            </ReportStatus>
        </Box>
    )
}
